import React, { useCallback, useEffect, useMemo } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useFormContext } from 'react-hook-form';
import styled from '@emotion/styled';
import { Input } from '@bucketplace/commerce-admin-ui';
import { FONT_STYLE } from '@bucketplace/design-system';
import { Popper } from '@bucketplace/design-system/bds';

import { useFieldForm } from '../../../hooks';
import { COLORS, validateHex } from '../../../styles';
import { FieldItem } from '../../FieldItem';
import { RgbInput } from './RgbInput';

type ChipWidth = number | string;
interface ColorPickerProps {
  name?: string;
  width?: ChipWidth;
  onValue?: (color: string) => void;
}

const HEX_LENGTH = 6;

export function ColorPickerField({
  name = 'color',
  width = 160,
  onValue,
}: ColorPickerProps) {
  const { watch, setValue } = useFormContext();
  const { fields, register, onChange: onChangeHexInput } = useFieldForm();
  const originColor = watch(name);
  const color = useMemo(
    () => (validateHex(originColor) ? originColor : '#ffffff').slice(1).toUpperCase(),
    [originColor],
  );

  const handleChange = useCallback((color: string) => {
    setValue(name, color);
  }, [setValue]);

  const handleChangeHex = (value: string) => {
    onChangeHexInput('hex_input', value?.replace(/[^A-Fa-f0-9]/i, '').toUpperCase());
  };

  const handleBlurInput = () => {
    setValue(name, `#${fields['hex_input']}`);
  };

  useEffect(() => {
    onChangeHexInput('hex_input', color);
    onValue?.(`#${color}`);
  }, [color]);

  useEffect(() => {
    register('hex_input', {
      defaultValue: color,
    });
  }, []);

  return (
    <Popper
      placement="left-end"
      renderButton={({ onClick }) => {
        return (
          <Chip
            type="button"
            color={color}
            width={width}
            onClick={onClick}
          >
            { `#${color}` }
          </Chip>
        );
      }}
    >
      <Container data-testid="color-picker-popper">
        <StyledHexColorPicker color={color} onChange={handleChange} />
        <InputContainer>
          <FieldItem label="#" direction="HORIZONTAL">
            <Input
              size={32}
              aria-label="color hex input"
              value={fields.hex_input}
              width="100%"
              maxLength={HEX_LENGTH}
              onChange={(event) => handleChangeHex(event.target.value)}
              onBlur={handleBlurInput}
            />
          </FieldItem>
          <RgbInput
            color={{
              r: parseInt(color.slice(0, 2), 16),
              g: parseInt(color.slice(2, 4), 16),
              b: parseInt(color.slice(4, 6), 16),
            }}
            onChange={handleChange}
          />
        </InputContainer>
      </Container>
    </Popper>
  );
}

const Container = styled.div`
  margin-right: 5px;
  width: 200px;
  border-radius: 8px;
  background: ${COLORS.gray0};
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
`;

const Chip = styled.button<{ color: string, width: ChipWidth }>`
  position: relative;
  ${FONT_STYLE.body14L20};
  border-radius: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.content.base1};
  border: 1px solid ${({ theme }) => theme.border};
  width: ${({ width }) => typeof width === 'number' ? `${width}px` : width};
  height: 32px;
  text-align: start;
  padding: 0 13px 0 37px;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 8px;
    bottom: 0;
    width: 12px;
    height: 12px;
    margin: auto;
    border-radius: 12px;
    background: ${({ color }) => `#${color}`};
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
  }
`;

const InputContainer = styled.section`
  > div {
    padding: 8px;
    color: ${({ theme }) => theme.content.base3};
  }
`;

const StyledHexColorPicker = styled(HexColorPicker)`
  &.react-colorful {
    width: 100%;
  }
`;
